import { useMemo, useState, useEffect } from 'react'
import { Card, Row, Col, DatePicker, Button, Space, Table, Tag, message, Empty, Spin } from 'antd'
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  LineChart,
  Line,
  CartesianGrid,
  ComposedChart,
} from 'recharts'
import type { Dayjs } from 'dayjs'
import { analyticsApi } from '../../services/api'
import Permission from '../../components/Permission'
import { loadSummaryFilters } from '../../utils/summaryFiltersCache'

const { RangePicker } = DatePicker

const Analytics = () => {
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null)
  const [appliedRange, setAppliedRange] = useState<[Dayjs, Dayjs] | null>(null)
  const [analyticsLoading, setAnalyticsLoading] = useState(false)
  const [reportGenerating, setReportGenerating] = useState(false)
  const [indicatorsFromApi, setIndicatorsFromApi] = useState<
    Array<{ key: string; metric: string; description?: string; frequency?: string; unit?: string; trend?: { time: string; value: number }[] }> | null
  >(null)
  const [assetDistributionData, setAssetDistributionData] = useState<any>(null)
  const [trendAnalysisData, setTrendAnalysisData] = useState<any>(null)
  const [comparisonAnalysisData, setComparisonAnalysisData] = useState<any>(null)
  const [amountDimensionData, setAmountDimensionData] = useState<any>(null)
  const [reportsListData, setReportsListData] = useState<any>(null)

  const dateRangeBody = useMemo(() => {
    const range = Array.isArray(appliedRange) && appliedRange.length >= 2 ? appliedRange : null
    const base: Record<string, unknown> = {
      dataSourceFilters: loadSummaryFilters(),
    }
    if (range) {
      base.dateRange = { from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD') }
    }
    return base
  }, [appliedRange])

  useEffect(() => {
    setAnalyticsLoading(true)
    const body = dateRangeBody
    Promise.all([
      analyticsApi.indicators(body),
      analyticsApi.assetDistribution(body),
      analyticsApi.trendAnalysis(body),
      analyticsApi.comparisonAnalysis(body),
      analyticsApi.amountDimension(body),
      analyticsApi.reportsList(body),
    ])
      .then(([indRes, distRes, trendRes, compareRes, amountRes, reportsRes]) => {
        const list = indRes?.data?.indicators
        setIndicatorsFromApi(Array.isArray(list) ? list : null)
        setAssetDistributionData(distRes?.data ?? null)
        setTrendAnalysisData(trendRes?.data ?? null)
        setComparisonAnalysisData(compareRes?.data ?? null)
        setAmountDimensionData(amountRes?.data ?? null)
        setReportsListData(reportsRes?.data ?? null)
      })
      .catch(() => {
        setIndicatorsFromApi(null)
        setAssetDistributionData(null)
        setTrendAnalysisData(null)
        setComparisonAnalysisData(null)
        setAmountDimensionData(null)
        setReportsListData(null)
      })
      .finally(() => setAnalyticsLoading(false))
  }, [dateRangeBody])

  const pieColors = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444']

  const assetTypeDataFinal = useMemo(
    () => (Array.isArray(assetDistributionData?.assetTypeData) ? assetDistributionData.assetTypeData : []),
    [assetDistributionData]
  )
  const assetStatusDataFinal = useMemo(
    () => (Array.isArray(assetDistributionData?.assetStatusData) ? assetDistributionData.assetStatusData : []),
    [assetDistributionData]
  )
  const assetSourceDataFinal = useMemo(
    () => (Array.isArray(assetDistributionData?.assetSourceData) ? assetDistributionData.assetSourceData : []),
    [assetDistributionData]
  )
  const regionDataFinal = useMemo(
    () => (Array.isArray(assetDistributionData?.regionData) ? assetDistributionData.regionData : []),
    [assetDistributionData]
  )

  const growthTrendDataFinal = useMemo(
    () => (Array.isArray(trendAnalysisData?.growthTrendData) ? trendAnalysisData.growthTrendData : []),
    [trendAnalysisData]
  )
  const amountTrendDataFinal = useMemo(
    () => (Array.isArray(trendAnalysisData?.amountTrendData) ? trendAnalysisData.amountTrendData : []),
    [trendAnalysisData]
  )
  const maturityTrendDataFinal = useMemo(
    () => (Array.isArray(trendAnalysisData?.maturityTrendData) ? trendAnalysisData.maturityTrendData : []),
    [trendAnalysisData]
  )

  const periodCompareDataFinal = useMemo(
    () => (Array.isArray(comparisonAnalysisData?.periodCompareData) ? comparisonAnalysisData.periodCompareData : []),
    [comparisonAnalysisData]
  )
  const categoryCompareDataFinal = useMemo(
    () => (Array.isArray(comparisonAnalysisData?.categoryCompareData) ? comparisonAnalysisData.categoryCompareData : []),
    [comparisonAnalysisData]
  )
  const ownerCompareDataFinal = useMemo(
    () => (Array.isArray(comparisonAnalysisData?.ownerCompareData) ? comparisonAnalysisData.ownerCompareData : []),
    [comparisonAnalysisData]
  )

  const amountDistributionDataFinal = useMemo(
    () => (Array.isArray(amountDimensionData?.amountDistributionData) ? amountDimensionData.amountDistributionData : []),
    [amountDimensionData]
  )
  const topAssetsFinal = useMemo(
    () => (Array.isArray(amountDimensionData?.topAssets) ? amountDimensionData.topAssets : []),
    [amountDimensionData]
  )
  const paretoDataFinal = useMemo(
    () => (Array.isArray(amountDimensionData?.paretoData) ? amountDimensionData.paretoData : []),
    [amountDimensionData]
  )

  const indicatorTrends = useMemo(() => {
    const list = Array.isArray(indicatorsFromApi) ? indicatorsFromApi : null
    if (list?.length) {
      const result: Record<string, { unit?: string; data: { time: string; value: number }[] }> = {}
      list.forEach((ind) => {
        result[ind.key] = {
          unit: ind.unit,
          data: Array.isArray(ind.trend) ? ind.trend : [],
        }
      })
      return result
    }
    return {}
  }, [indicatorsFromApi])

  return (
    <div className="space-y-6">
      {/* 页面标题和操作 */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold mb-2">分析报告</h1>
          <p className="text-gray-600">从多维度分析数字资产运行情况</p>
        </div>
        <Space>
          <RangePicker
            value={dateRange}
            onChange={(values) => setDateRange(values as [Dayjs, Dayjs] | null)}
            allowClear
          />
          <Button
            type="primary"
            disabled={!dateRange}
            loading={analyticsLoading}
            onClick={() => {
              if (dateRange) setAppliedRange(dateRange)
            }}
          >
            生成分析
          </Button>
          {reportsListData != null && (
            <span className="text-gray-500 text-sm">
              共 {Array.isArray(reportsListData?.items) ? reportsListData.items.length : (reportsListData?.total ?? 0)} 份报告
            </span>
          )}
          <Permission code="analytics:reports_create">
            <Button
              loading={reportGenerating}
              onClick={() => {
                setReportGenerating(true)
                analyticsApi.reportsCreate(
                  Array.isArray(appliedRange) && appliedRange.length >= 2
                    ? { dateRange: { from: appliedRange[0].format('YYYY-MM-DD'), to: appliedRange[1].format('YYYY-MM-DD') } }
                    : {}
                )
                  .then(() => message.success('报告生成成功'))
                  .catch(() => message.info('报告生成请求已发送，请稍后在报告中查看'))
                  .finally(() => setReportGenerating(false))
              }}
            >
              生成报告
            </Button>
          </Permission>
        </Space>
      </div>

      {/* 资产产品运行指标 */}
      <Card title="资产产品运行指标">
        <Row gutter={[16, 16]} className="mb-6">
          {(Array.isArray(indicatorsFromApi) ? indicatorsFromApi : []).map((item, index) => {
            const trend = indicatorTrends[item.key]
            const latestValue = trend?.data[trend.data.length - 1]?.value

            return (
              <Col key={item.key} xs={24} md={12} lg={8}>
                <div className="h-56 flex flex-col">
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-medium text-gray-800">{item.metric}</div>
                    <Tag color="blue" className="text-xs px-2 py-0">
                      {item.frequency}
                    </Tag>
          </div>
                  {/* 后端未返回或值为 0 时不显示数字，空着 */}
                  {(latestValue !== undefined && latestValue !== 0) && (
                    <div className="text-2xl font-semibold text-gray-900 mb-2">
                      {item.key === 'totalValue'
                        ? latestValue.toLocaleString('zh-CN', { maximumFractionDigits: 2 })
                        : latestValue}
                      {trend?.unit && (
                        <span className="text-sm text-gray-500 ml-1">{trend.unit}</span>
                      )}
                    </div>
                  )}
          <div className="flex-1">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={trend?.data || []}
                        margin={{ top: 8, right: 8, bottom: 0, left: -16 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="time" hide />
                        <YAxis hide />
                        <Tooltip
                          formatter={(value: number) =>
                            trend?.unit ? `${value} ${trend.unit}` : value
                          }
                        />
                        <Line
                          type="monotone"
                          dataKey="value"
                          stroke={pieColors[index % pieColors.length]}
                          strokeWidth={2}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
          </div>
        </div>
              </Col>
            )
          })}
        </Row>
        {!analyticsLoading && (!Array.isArray(indicatorsFromApi) || indicatorsFromApi.length === 0) && (
          <div className="py-10">
            <Empty description="暂无运行指标数据" />
          </div>
        )}
      </Card>

      {/* 资产产品分布分析 */}
      <Card title="资产数据分布分析">
      <Row gutter={[16, 28]}>
          <Col xs={24} lg={12}>
            <div className="h-72">
              <div className="text-sm text-gray-500 mb-2">按类型分布（数量 & 金额占比）</div>
              {assetTypeDataFinal.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={assetTypeDataFinal}
                      dataKey="amount"
                      nameKey="type"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label
                    >
                      {assetTypeDataFinal.map((entry: { type: string }, index: number) => (
                        <Cell key={entry.type} fill={pieColors[index % pieColors.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `${value} 万`} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : analyticsLoading ? (
                <div className="h-full flex items-center justify-center">
                  <Spin />
                </div>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <Empty description="暂无数据" />
                </div>
              )}
            </div>
        </Col>
          <Col xs={24} lg={12}>
            <div className="h-72">
              <div className="text-sm text-gray-500 mb-2">按状态分布（资产数量）</div>
              {assetStatusDataFinal.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={assetStatusDataFinal} margin={{ top: 16, right: 16, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="status" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" name="数量" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              ) : analyticsLoading ? (
                <div className="h-full flex items-center justify-center">
                  <Spin />
                </div>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <Empty description="暂无数据" />
                </div>
              )}
            </div>
        </Col>
      </Row>

        <Row gutter={[16, 28]} className="mt-8 mb-6">
          <Col xs={24} lg={12}>
            <div className="h-72">
              <div className="text-sm text-gray-500 mb-2">按来源分布（数量 & 金额）</div>
              {assetSourceDataFinal.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={assetSourceDataFinal}
                    layout="vertical"
                    margin={{ top: 16, right: 16, bottom: 0, left: 40 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="source" type="category" />
                    <Tooltip formatter={(value, name) => (name === '金额' ? `${value} 万` : value)} />
                    <Legend />
                    <Bar dataKey="count" name="数量" fill="#10b981" />
                    <Bar dataKey="amount" name="金额(万)" fill="#8b5cf6" />
                  </BarChart>
                </ResponsiveContainer>
              ) : analyticsLoading ? (
                <div className="h-full flex items-center justify-center">
                  <Spin />
                </div>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <Empty description="暂无数据" />
                </div>
              )}
                </div>
              </Col>
              <Col xs={24} lg={12}>
            <div className="h-72">
              <div className="text-sm text-gray-500 mb-2">地理分布（地区 & 金额）</div>
              {regionDataFinal.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={regionDataFinal} margin={{ top: 16, right: 16, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="region" />
                    <YAxis />
                    <Tooltip formatter={(value) => `${value} 万`} />
                    <Bar dataKey="amount" name="金额(万)" fill="#f59e0b" />
                  </BarChart>
                </ResponsiveContainer>
              ) : analyticsLoading ? (
                <div className="h-full flex items-center justify-center">
                  <Spin />
                </div>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <Empty description="暂无数据" />
                </div>
              )}
                </div>
              </Col>
            </Row>
      </Card>

      {/* 趋势分析 */}
      <Card title="趋势分析">
            <Row gutter={[16, 28]}>
              <Col xs={24} lg={12}>
            <div className="h-72">
              <div className="text-sm text-gray-500 mb-2">资产增长趋势（数量 & 总金额）</div>
              {growthTrendDataFinal.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={growthTrendDataFinal} margin={{ top: 16, right: 16, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip formatter={(value, name) => (name === '总金额(万)' ? `${value} 万` : value)} />
                    <Legend />
                    <Bar yAxisId="left" dataKey="assets" name="资产数量" fill="#3b82f6" />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="amount"
                      name="总金额(万)"
                      stroke="#10b981"
                      strokeWidth={2}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : analyticsLoading ? (
                <div className="h-full flex items-center justify-center">
                  <Spin />
                </div>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <Empty description="暂无数据" />
                </div>
              )}
                </div>
              </Col>
              <Col xs={24} lg={12}>
            <div className="h-72">
              <div className="text-sm text-gray-500 mb-2">金额趋势（总金额 / 平均金额 / 最大金额）</div>
              {amountTrendDataFinal.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={amountTrendDataFinal} margin={{ top: 16, right: 16, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="total" name="总金额(万)" stroke="#3b82f6" strokeWidth={2} />
                    <Line type="monotone" dataKey="avg" name="平均金额(万)" stroke="#10b981" />
                    <Line type="monotone" dataKey="max" name="最大金额(万)" stroke="#f59e0b" />
                  </LineChart>
                </ResponsiveContainer>
              ) : analyticsLoading ? (
                <div className="h-full flex items-center justify-center">
                  <Spin />
                </div>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <Empty description="暂无数据" />
                </div>
              )}
                </div>
              </Col>
            </Row>

        <Row gutter={[16, 28]} className="mt-8 mb-6">
          <Col xs={24} lg={12}>
            <div className="h-72">
              <div className="text-sm text-gray-500 mb-2">到期趋势（未来一段时间内到期资产）</div>
              {maturityTrendDataFinal.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={maturityTrendDataFinal} margin={{ top: 16, right: 16, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip formatter={(value, name) => (name === '金额' ? `${value} 万` : value)} />
                    <Legend />
                    <Bar yAxisId="left" dataKey="count" name="到期数量" fill="#8b5cf6" />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="amount"
                      name="到期金额(万)"
                      stroke="#ef4444"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : analyticsLoading ? (
                <div className="h-full flex items-center justify-center">
                  <Spin />
                </div>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <Empty description="暂无数据" />
                </div>
              )}
            </div>
          </Col>
        </Row>
                </Card>
                
      {/* 对比分析 */}
      <Card title="对比分析">
        <Row gutter={[16, 28]} className="mb-6">
          <Col xs={24} lg={8}>
            <div className="h-72">
              <div className="text-sm text-gray-500 mb-2">周期对比（本月 / 上月 / 去年同期）</div>
              {periodCompareDataFinal.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={periodCompareDataFinal} margin={{ top: 16, right: 16, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis />
                    <Tooltip formatter={(value, name) => (name === '金额' ? `${value} 万` : value)} />
                    <Legend />
                    <Bar dataKey="assets" name="资产数量" fill="#3b82f6" />
                    <Bar dataKey="amount" name="金额(万)" fill="#10b981" />
                  </BarChart>
                </ResponsiveContainer>
              ) : analyticsLoading ? (
                <div className="h-full flex items-center justify-center">
                  <Spin />
                </div>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <Empty description="暂无数据" />
                </div>
              )}
                            </div>
          </Col>
          <Col xs={24} lg={8}>
            <div className="h-72">
              <div className="text-sm text-gray-500 mb-2">分类对比（各类型平均收款率 %）</div>
              {categoryCompareDataFinal.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={categoryCompareDataFinal} margin={{ top: 16, right: 16, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="category" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="indicator" name="收款率(%)" fill="#f59e0b" />
                  </BarChart>
                </ResponsiveContainer>
              ) : analyticsLoading ? (
                <div className="h-full flex items-center justify-center">
                  <Spin />
                </div>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <Empty description="暂无数据" />
                </div>
              )}
              </div>
          </Col>
          <Col xs={24} lg={8}>
            <div className="h-72">
              <div className="text-sm text-gray-500 mb-2">客户对比（资产数量 & 金额）</div>
              {ownerCompareDataFinal.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={ownerCompareDataFinal} margin={{ top: 16, right: 16, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="owner" />
                    <YAxis />
                    <Tooltip formatter={(value, name) => (name === '金额' ? `${value} 万` : value)} />
                    <Legend />
                    <Bar dataKey="assets" name="资产数量" fill="#8b5cf6" />
                    <Bar dataKey="amount" name="金额(万)" fill="#ef4444" />
                  </BarChart>
                </ResponsiveContainer>
              ) : analyticsLoading ? (
                <div className="h-full flex items-center justify-center">
                  <Spin />
                </div>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <Empty description="暂无数据" />
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Card>

      {/* 金额维度分析 */}
      <Card title="金额维度分析">
        <Row gutter={[16, 28]} className="mb-6">
          <Col xs={24} lg={8}>
            <div className="h-72">
              <div className="text-sm text-gray-500 mb-2">金额分布（直方图）</div>
              {amountDistributionDataFinal.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={amountDistributionDataFinal} margin={{ top: 16, right: 16, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="range" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" name="资产数量" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              ) : analyticsLoading ? (
                <div className="h-full flex items-center justify-center">
                  <Spin />
                </div>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <Empty description="暂无数据" />
                </div>
              )}
        </div>
          </Col>

          <Col xs={24} lg={8}>
            <div className="h-72">
              <div className="text-sm text-gray-500 mb-2">金额排名（Top 10 资产）</div>
              <Table
                size="small"
                pagination={false}
                dataSource={topAssetsFinal}
                rowKey="id"
                columns={[
                  {
                    title: '排名',
                    dataIndex: 'rank',
                    width: 60,
                  },
                  {
                    title: '资产名称',
                    dataIndex: 'name',
                    ellipsis: true,
                  },
                  {
                    title: '类型',
                    dataIndex: 'type',
                    width: 100,
                    render: (value: string) => <Tag color="blue">{value}</Tag>,
                  },
                  {
                    title: '金额(万)',
                    dataIndex: 'amount',
                    width: 90,
                  },
                ]}
              />
              {!topAssetsFinal.length && (
                <div className="py-8">
                  {analyticsLoading ? <Spin /> : <Empty description="暂无数据" />}
                </div>
              )}
            </div>
          </Col>

          <Col xs={24} lg={8}>
            <div className="h-72">
              <div className="text-sm text-gray-500 mb-2">金额累计（帕累托曲线）</div>
              {paretoDataFinal.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={paretoDataFinal} margin={{ top: 16, right: 16, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="bucket" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" domain={[0, 100]} />
                    <Tooltip formatter={(value, name) => (name === '累计占比' ? `${value}%` : `${value} 万`)} />
                    <Legend />
                    <Bar yAxisId="left" dataKey="amount" name="金额(万)" fill="#10b981" />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="cumulativeRatio"
                      name="累计占比"
                      stroke="#ef4444"
                      strokeWidth={2}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : analyticsLoading ? (
                <div className="h-full flex items-center justify-center">
                  <Spin />
                </div>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <Empty description="暂无数据" />
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  )
}

export default Analytics